import { createSlice } from "@reduxjs/toolkit";
import { parseUrlParams } from "shared/helpers/urlHelpers";
import { KrpanoState, LayerVisibilityUpdate } from "./krpanoStateModels";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";

const showFields = parseUrlParams<boolean>("showfields", true, false);

const initialState: KrpanoState = {
  autoRotate: parseUrlParams<KrpanoState["autoRotate"]>(
    "autorotate",
    false,
    true
  ),
  measureMode: null,
  layers: {
    // Field layers only
    fields: [
      {
        orderNumber: 1,
        name: "poi_spot",
        targetLayer: "poi_spot",
        visible: parseUrlParams<boolean>("showpoi", showFields, false),
      },
      {
        orderNumber: 2,
        name: "boundary_line",
        targetLayer: "boundary_line",
        visible: parseUrlParams<boolean>("showboundary", showFields, false),
      },
      {
        orderNumber: 3,
        name: "field_marker",
        targetLayer: "field_marker",
        visible: parseUrlParams<boolean>("showfieldmarker", showFields, false),
      },
    ],
    // Additional features will be added here
    features: [],
  },
  showMap: parseUrlParams<KrpanoState["showMap"]>("showmap", false, true),
  plugins: {
    zfilter: {
      gamma: 2.2,
      contrast: 0,
      exposure: 0,
      saturate: 0,
      invert: 0,
      rgbvi: 0,
      /** See krpano.xml for default value - Field Boundary Line */
      rgbvimode: "ExGR",
      /** See krpano.xml for default value - Field Boundary Line */
      mask: 1,
      /** See krpano.xml for default value - Field Boundary Line */
      maskelement: "hotspot[boundary_line]",
    },
  },
  topDownView: parseUrlParams<KrpanoState["topDownView"]>(
    "topdownview",
    false,
    true
  ),
};

const settingsSlice = createSlice({
  name: "settingsSlice",
  initialState,
  reducers: {
    /**
     * Update auto-rotate mode, this must correspond with krpano
     */
    setAutoRotate(state, action: { payload: KrpanoState["autoRotate"] }) {
      state.autoRotate = action.payload;
    },
    /**
     * Update measure mode, this must correspond with krpano
     */
    setMeasureMode(state, action: { payload: KrpanoState["measureMode"] }) {
      state.measureMode = action.payload;
    },
    /**
     * Update if the map is open, this must correspond with krpano
     */
    setShowMap(state, action: { payload: KrpanoState["showMap"] }) {
      state.showMap = action.payload;
    },
    // /**
    //  * Add new layers to the existing ones, to update layer visibility see
    //  * `setLayerVisibility`
    //  */
    // updateLayers(state, action: { payload: KrpanoState["layers"] }) {
    //   state.layers.push(...action.payload);
    // },
    /**
     * Add new layers to the existing ones, to update layer visibility see
     * `setLayerVisibility`
     */
    addFeatures(state, action: { payload: KrpanoState["layers"]["features"] }) {
      state.layers.features = action.payload;
    },
    /**
     * Update the visibility of one or more layers
     * This accepts an array in order to allow multiple changes at once
     *
     * @note The structure of the payload will determine the way we update the
     * layer visibility.
     * e.g. If updating `boundary_line`, the payload will contain
     * `type: "fields", `targetIndex`, and `visible` (of course)
     */
    setLayerVisibility(
      state,
      action: {
        payload: LayerVisibilityUpdate[];
      }
    ) {
      action.payload.forEach((update) => {
        if (notNullNorUndefined(update.targetIndex)) {
          state.layers[update.type][update.targetIndex].visible =
            update.visible;
        } else {
          state.layers[update.type].forEach(
            (fieldLayer) => (fieldLayer.visible = update.visible)
          );
        }
      });
    },
    /**
     * Restore the layer visibility to the initial state
     * This is done when changing fields, as the new field will have the
     * initial visibility state _plus_ any new features
     */
    resetLayerVisibility(state) {
      state.layers = initialState.layers;
    },
    /**
     *
     * @param state
     * @param action
     */
    setFilters(
      state,
      action: {
        payload: Partial<
          Record<
            keyof KrpanoState["plugins"]["zfilter"],
            KrpanoState["plugins"]["zfilter"][keyof KrpanoState["plugins"]["zfilter"]]
          >
        >;
      }
    ) {
      Object.entries(action.payload).forEach(([key, value]) => {
        // state.plugins.zfilter[key as keyof KrpanoState["plugins"]["zfilter"]] = value as typeof state.plugins.zfilter[keyof typeof state.plugins.zfilter];
        (state.plugins.zfilter[
          key as keyof typeof state.plugins.zfilter
        ] as (typeof state.plugins.zfilter)[keyof typeof state.plugins.zfilter]) =
          value;
        // Set in krpano
        // Can't do this here
        // if (plugin) {
        //   (plugin as (typeof plugin)[typeof key])[key] = value;
        // } else {
        //   console.warn("zfilter plugin not found");
        // }
      });
    },
    setTopDownView(state, action: { payload: KrpanoState["topDownView"] }) {
      state.topDownView = action.payload;
    },
  },
});

export const {
  setAutoRotate,
  setMeasureMode,
  setShowMap,
  addFeatures,
  setLayerVisibility,
  resetLayerVisibility,
  setFilters,
  setTopDownView,
} = settingsSlice.actions;

export default settingsSlice.reducer;
